import { Controller } from "@hotwired/stimulus"
import { UserAgent, Registerer, SessionState } from "sip.js"

// Softphone WebRTC — rejestracja SIP przez WSS i odbieranie połączeń.
// Wymaga: data-softphone-credentials-url (endpoint JSON z danymi SIP)
export default class extends Controller {
  static targets = [
    "status", "callerInfo", "callActions", "connectBtn",
    "disconnectBtn", "answerBtn", "hangupBtn", "sipSelect",
    "timer", "log",
  ]

  connect() {
    this.timerInterval = null
    this.callStartTime = null
    this.audioElement = null

    // przywróć stan SIP jeśli Turbo navigation
    const saved = window._softphoneState
    if (saved && saved.ua) {
      this.ua = saved.ua
      this.registerer = saved.registerer
      this.currentSession = saved.currentSession
      this.currentSipId = saved.currentSipId
      this.currentCallUuid = saved.currentCallUuid
      window._softphoneState = null

      // zaktualizuj UI
      this.ua.delegate = {
        onInvite: (invitation) => this.handleIncoming(invitation),
      }
      this.setStatus("registered", "Zarejestrowany — czekam na połączenia")
      this.connectBtnTarget.classList.add("hidden")
      this.disconnectBtnTarget.classList.remove("hidden")
      if (this.hasSipSelectTarget) this.sipSelectTarget.value = this.currentSipId
      this.addLog("Przywrócono połączenie SIP")
    } else {
      this.ua = null
      this.registerer = null
      this.currentSession = null
      this.currentCallUuid = null
      this.currentSipId = null
    }

    this.requestNotificationPermission()

    // rozłącz SIP dopiero przy zamknięciu okna/taba
    window.addEventListener("beforeunload", this.boundBeforeUnload = () => {
      this.cleanup()
    })

    // auto-connect z URL param (popup)
    const urlParams = new URLSearchParams(window.location.search)
    const autoConnect = urlParams.get("auto_connect")
    if (autoConnect && !this.ua) {
      this.sipSelectTarget.value = autoConnect
      setTimeout(() => this.doConnect(), 300)
    }
  }

  disconnect() {
    // nie rozłączaj SIP przy Turbo nawigacji — tylko przy zamknięciu okna/taba
    // Turbo wywołuje disconnect() przy każdej nawigacji
    if (this.ua) {
      // zachowaj referencję globalnie żeby przetrwało Turbo navigation
      window._softphoneState = {
        ua: this.ua,
        registerer: this.registerer,
        currentSession: this.currentSession,
        currentSipId: this.currentSipId,
        currentCallUuid: this.currentCallUuid,
      }
    }
    this.stopTimer()
  }

  // podłączenie do SuperVoIP WSS
  async doConnect() {
    const sipId = this.sipSelectTarget.value
    if (!sipId) return

    this.setStatus("connecting", "Łączenie...")
    this.connectBtnTarget.disabled = true

    try {
      this.currentSipId = sipId
      const resp = await fetch(`/voip/softphone/credentials.json?sip_id=${sipId}`)
      if (!resp.ok) throw new Error("Nie udało się pobrać danych SIP")
      const creds = await resp.json()

      this.addLog(`Łączenie z ${creds.wss_server} jako ${creds.username}...`)

      this.ua = new UserAgent({
        uri: UserAgent.makeURI(creds.sip_uri),
        transportOptions: {
          server: creds.wss_server,
        },
        authorizationUsername: creds.username,
        authorizationPassword: creds.password,
        displayName: creds.display_name || creds.username,
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: {
            iceServers: [{ urls: creds.stun_server }],
          },
        },
        logLevel: "warn",
      })

      // nasłuchiwanie połączeń przychodzących
      this.ua.delegate = {
        onInvite: (invitation) => this.handleIncoming(invitation),
        onDisconnect: (error) => {
          if (error) {
            this.addLog(`Rozłączono: ${error.message}`)
            this.setStatus("error", "Rozłączono")
          }
        },
      }

      await this.ua.start()

      this.registerer = new Registerer(this.ua, {
        expires: 300,
      })

      this.registerer.stateChange.addListener((state) => {
        this.addLog(`Rejestracja: ${state}`)
        if (state === "Registered") {
          this.setStatus("registered", "Zarejestrowany — czekam na połączenia")
          this.disconnectBtnTarget.classList.remove("hidden")
          this.connectBtnTarget.classList.add("hidden")
        } else if (state === "Unregistered") {
          this.setStatus("disconnected", "Niezarejestrowany")
          this.disconnectBtnTarget.classList.add("hidden")
          this.connectBtnTarget.classList.remove("hidden")
          this.connectBtnTarget.disabled = false
        }
      })

      await this.registerer.register()
    } catch (e) {
      this.addLog(`Błąd: ${e.message}`)
      this.setStatus("error", `Błąd: ${e.message}`)
      this.connectBtnTarget.disabled = false
    }
  }

  // rozłączenie
  async doDisconnect() {
    await this.cleanup()
    this.setStatus("disconnected", "Rozłączony")
    this.disconnectBtnTarget.classList.add("hidden")
    this.connectBtnTarget.classList.remove("hidden")
    this.connectBtnTarget.disabled = false
  }

  // obsługa połączenia przychodzącego
  handleIncoming(invitation) {
    const callerNumber = invitation.remoteIdentity.uri.user
    this.addLog(`Połączenie przychodzące od: ${callerNumber}`)
    this.currentSession = invitation
    const caller = invitation.remoteIdentity.displayName || callerNumber
    this.callerInfoTarget.textContent = `Dzwoni: ${caller}`
    this.callerInfoTarget.classList.remove("hidden")
    this.setStatus("ringing", `Połączenie przychodzące: ${caller}`)

    // generuj UUID i loguj do bazy
    this.currentCallUuid = `softphone_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    this.logCall("ringing", { caller_number: callerNumber })
    this.showDesktopNotification(caller)

    // pokaż przycisk Odbierz
    this.answerBtnTarget.classList.remove("hidden")
    this.hangupBtnTarget.classList.remove("hidden")
    this.callActionsTarget.classList.remove("hidden")

    // obsługa zmiany stanu sesji
    invitation.stateChange.addListener((state) => {
      this.addLog(`Sesja: ${state}`)
      if (state === SessionState.Established) {
        this.onCallEstablished()
      } else if (state === SessionState.Terminated) {
        this.onCallEnded()
      }
    })

    // auto-play dzwonek (systemowy beep)
    this.playRingtone()
  }

  // odbierz połączenie
  async answerCall() {
    if (!this.currentSession) return

    this.stopRingtone()
    this.addLog("Odbieranie połączenia...")

    try {
      await this.currentSession.accept({
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: true,
            video: false,
          },
        },
      })
    } catch (e) {
      this.addLog(`Błąd odbierania: ${e.message}`)
    }
  }

  // rozłącz aktualne połączenie
  hangupCall() {
    if (!this.currentSession) return

    this.stopRingtone()
    this.addLog("Rozłączanie...")

    try {
      const state = this.currentSession.state
      if (state === SessionState.Established) {
        this.currentSession.bye()
      } else if (state === SessionState.Initial || state === SessionState.Establishing) {
        this.currentSession.reject()
      }
    } catch (e) {
      this.addLog(`Błąd rozłączania: ${e.message}`)
    }
  }

  // połączenie nawiązane
  onCallEstablished() {
    this.setStatus("in_call", "Rozmowa w toku")
    this.answerBtnTarget.classList.add("hidden")
    this.startTimer()
    this.setupAudio()
    this.logCall("answered")
    this.closeDesktopNotification()
  }

  // połączenie zakończone
  onCallEnded() {
    this.stopRingtone()
    this.closeDesktopNotification()
    const duration = this.callStartTime ? Math.floor((Date.now() - this.callStartTime) / 1000) : 0
    this.stopTimer()
    this.cleanupAudio()
    this.logCall("ended", { duration })
    this.currentSession = null
    this.currentCallUuid = null
    this.callerInfoTarget.classList.add("hidden")
    this.callActionsTarget.classList.add("hidden")
    this.setStatus("registered", "Zarejestrowany — czekam na połączenia")
    this.addLog("Połączenie zakończone")
  }

  // podłączenie audio z WebRTC do <audio>
  setupAudio() {
    if (!this.currentSession) return

    const sdh = this.currentSession.sessionDescriptionHandler
    if (!sdh) return

    // sip.js 0.21 — peerConnection dostępny przez sessionDescriptionHandler
    const pc = sdh.peerConnection
    if (!pc) return

    if (!this.audioElement) {
      this.audioElement = document.createElement("audio")
      this.audioElement.autoplay = true
      document.body.appendChild(this.audioElement)
    }

    const remoteStream = new MediaStream()
    pc.getReceivers().forEach((receiver) => {
      if (receiver.track) {
        remoteStream.addTrack(receiver.track)
      }
    })
    this.audioElement.srcObject = remoteStream
  }

  cleanupAudio() {
    if (this.audioElement) {
      this.audioElement.srcObject = null
      this.audioElement.remove()
      this.audioElement = null
    }
  }

  // timer rozmowy
  startTimer() {
    this.callStartTime = Date.now()
    this.timerTarget.classList.remove("hidden")
    this.timerInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - this.callStartTime) / 1000)
      const m = String(Math.floor(elapsed / 60)).padStart(2, "0")
      const s = String(elapsed % 60).padStart(2, "0")
      this.timerTarget.textContent = `${m}:${s}`
    }, 1000)
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
    if (this.hasTimerTarget) {
      this.timerTarget.classList.add("hidden")
      this.timerTarget.textContent = "00:00"
    }
    this.callStartTime = null
  }

  // prosty dzwonek — beep przez Web Audio API
  playRingtone() {
    this.stopRingtone()
    try {
      this.ringtoneCtx = new AudioContext()
      this.ringtoneLoop()
    } catch (_e) {
      // fallback: brak dzwonka
    }
  }

  ringtoneLoop() {
    if (!this.ringtoneCtx) return
    const ctx = this.ringtoneCtx
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.frequency.value = 440
    gain.gain.value = 0.3
    osc.start()
    osc.stop(ctx.currentTime + 0.5)
    this.ringtoneTimeout = setTimeout(() => this.ringtoneLoop(), 1500)
  }

  stopRingtone() {
    if (this.ringtoneCtx) {
      this.ringtoneCtx.close().catch(() => {})
      this.ringtoneCtx = null
    }
    if (this.ringtoneTimeout) {
      clearTimeout(this.ringtoneTimeout)
      this.ringtoneTimeout = null
    }
  }

  // otwórz softphone w osobnym mini-oknie z auto-connect
  openPopup() {
    const sipId = this.sipSelectTarget.value
    const w = 420
    const h = 600
    const left = screen.width - w - 20
    const top = 80
    const url = sipId ? `${window.location.pathname}?auto_connect=${sipId}` : window.location.pathname
    window.open(
      url,
      "softphone",
      `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`
    )
  }

  // powiadomienia przeglądarki
  requestNotificationPermission() {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission()
    }
  }

  showDesktopNotification(caller) {
    if ("Notification" in window && Notification.permission === "granted") {
      const n = new Notification("Połączenie przychodzące", {
        body: `Dzwoni: ${caller}`,
        icon: "/favicon.ico",
        tag: "softphone-call",
        requireInteraction: true,
      })
      n.onclick = () => {
        window.focus()
        n.close()
      }
      this.currentNotification = n
    }
  }

  closeDesktopNotification() {
    if (this.currentNotification) {
      this.currentNotification.close()
      this.currentNotification = null
    }
  }

  // logowanie połączenia do bazy
  async logCall(event, extra = {}) {
    if (!this.currentSipId || !this.currentCallUuid) return

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const body = {
        sip_id: this.currentSipId,
        event,
        call_id: this.currentCallUuid,
        ...extra,
      }
      await fetch("/voip/softphone/log_call.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify(body),
      })
    } catch (e) {
      this.addLog(`Log error: ${e.message}`)
    }
  }

  // UI helpers
  setStatus(state, text) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text

    const colors = {
      disconnected: "bg-gray-100 text-gray-600",
      connecting: "bg-yellow-100 text-yellow-700",
      registered: "bg-green-100 text-green-700",
      ringing: "bg-blue-100 text-blue-700 animate-pulse",
      in_call: "bg-green-100 text-green-700",
      error: "bg-red-100 text-red-700",
    }
    this.statusTarget.className = `inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium ${colors[state] || colors.disconnected}`
  }

  addLog(msg) {
    if (!this.hasLogTarget) return
    const time = new Date().toLocaleTimeString("pl-PL")
    const entry = document.createElement("div")
    entry.className = "text-xs text-gray-500 py-0.5"
    entry.textContent = `[${time}] ${msg}`
    this.logTarget.prepend(entry)

    // max 50 wpisów
    while (this.logTarget.children.length > 50) {
      this.logTarget.lastChild.remove()
    }
  }

  async cleanup() {
    this.stopRingtone()
    this.stopTimer()
    this.cleanupAudio()
    if (this.currentSession) {
      try { this.currentSession.bye() } catch (_e) { /* ok */ }
      this.currentSession = null
    }
    if (this.registerer) {
      try { await this.registerer.unregister() } catch (_e) { /* ok */ }
      this.registerer = null
    }
    if (this.ua) {
      try { await this.ua.stop() } catch (_e) { /* ok */ }
      this.ua = null
    }
  }
}
